import React, { createContext, ReactNode, useMemo } from 'react'
import {
  CreateNewRoute,
  LoadAccountMemberList, LoadCurrentAccount, LoadNewRouteSuggestion, LoadWorkList
} from '@/javascript/domain/usecases'
import useRoutingActiveStep from './hooks/use-routing-active-step'
import useRoutingConfig from './hooks/use-routing-config'
import useRoutingWorks from './hooks/use-routing-works'
import useRoutingMembers from './hooks/use-routing-members'
import useRoutingSuggestions from './hooks/use-routing-suggestions'

type RoutingContextProps =
  ReturnType<typeof useRoutingActiveStep> &
  ReturnType<typeof useRoutingConfig> &
  ReturnType<typeof useRoutingWorks> &
  ReturnType<typeof useRoutingMembers> &
  ReturnType<typeof useRoutingSuggestions> &
  Omit<RoutingProviderProps, 'children' | 'loadAccount'>

const RoutingContext = createContext<RoutingContextProps | null>(null)

type RoutingProviderProps = {
  loadRouteSuggestion: LoadNewRouteSuggestion
  loadAccount: LoadCurrentAccount
  loadAccountMemberList: LoadAccountMemberList
  loadWorkList: LoadWorkList
  createNewRoute: CreateNewRoute
  children: ReactNode
}

export const RoutingProvider: React.FC<RoutingProviderProps> = ({
  loadRouteSuggestion, loadAccount, loadAccountMemberList, loadWorkList, createNewRoute, children
}) => {
  const activeStep = useRoutingActiveStep()
  const config = useRoutingConfig(loadAccount)
  const works = useRoutingWorks()
  const members = useRoutingMembers()
  const suggestions = useRoutingSuggestions()

  const value: RoutingContextProps = useMemo(() => ({
    ...activeStep, ...config, ...works, ...members, ...suggestions,
    loadRouteSuggestion, loadAccountMemberList, loadWorkList, createNewRoute
  }), [
    activeStep.activeStep,
    config.accountInfo, config.date, config.workingHours,
    works.selectedWorks, works.workDurations, works.durationErrors,
    members.selectedMembers, members.memberConfigurations,
    suggestions.routeSuggestions, suggestions.unassignedWorkIds,
    suggestions.unassignedMemberIds, suggestions.selectionError,
    loadRouteSuggestion, loadAccountMemberList, loadWorkList, createNewRoute,
  ])

  return (
    <RoutingContext.Provider value={value}>
      {children}
    </RoutingContext.Provider>
  )
}

export default RoutingContext
